import type { FC } from 'react';
import { FullStarIcon } from 'src/components/shared/elements/icons/FullStarIcon';
import { HalfStarIcon } from 'src/components/shared/elements/icons/HalfStarIcon';

export const calculateStars = (rating: number) => {
  const originStars = Math.min(Math.max(0, rating), 5);
  const stars = Math.floor(originStars * 2) / 2;

  const full = Math.floor(stars);
  const half = stars % 1 > 0 ? 1 : 0;
  const empty = 5 - full - half;

  return { full, half, empty };
};

const renderStars = (count: number, type: 'full' | 'half' | 'empty', className?: string) =>
  Array(count)
    .fill(0)
    .map((_, i) => {
      if (type === 'full' || type === 'empty') {
        return (
          <FullStarIcon
            className={className}
            fillColor={type === 'empty' ? '#DADDE0' : undefined}
            label={type}
            key={`${type}${i}`}
          />
        );
      }
      return <HalfStarIcon className={className} label={type} key={`${type}${i}`} />;
    });

type Props = { rating: number | null; count?: number; withRating?: boolean; className?: string };

export const RatingStars: FC<Props> = ({ rating, count, withRating, className }) => {
  const stars = calculateStars(rating ?? 0);

  return (
    <div className="flex gap-1 items-center">
      {withRating && typeof rating === 'number' && (
        <span className="font-medium text-xs leading-[16.8px]" data-testid="rating-text">
          {rating.toFixed(1)}
        </span>
      )}
      <div className="flex items-center">
        {renderStars(stars.full, 'full', className)}
        {renderStars(stars.half, 'half', className)}
        {renderStars(stars.empty, 'empty', className)}
      </div>
      {typeof count === 'number' && (
        <span className="font-medium text-xs leading-[16.8px]" data-testid="count-text">
          ({count})
        </span>
      )}
    </div>
  );
};
